import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { fetchPackages, selectPackages, selectSubscriptionLoading } from "../../slices/subscriptionSlice";
import type { SubscriptionPackage } from "../../types/subscription";
import PricingCard from "./PricingCard";

interface PricingSectionProps {
  isAuthenticated: boolean;
  onSelectPlan: (pkg: SubscriptionPackage) => void;
}

const PricingSection = ({ isAuthenticated, onSelectPlan }: PricingSectionProps) => {
  const dispatch = useAppDispatch();
  const packages = useAppSelector(selectPackages);
  const loading = useAppSelector(selectSubscriptionLoading);

  useEffect(() => {
    if (!packages || packages.length === 0) {
      dispatch(fetchPackages(null));
    }
  }, [dispatch, packages]);

  const bestPackageId = useMemo(() => {
    if (!packages?.length) return null;
    let best = packages[0];
    let bestLimit = -1;
    packages.forEach((p: SubscriptionPackage) => {
      const details = p.PackageDetails?.[0];
      const val = details?.monthlySalesLimit === null ? Number.POSITIVE_INFINITY : Number(details?.employeeLimit ?? 0);
      if (val > bestLimit) {
        bestLimit = val;
        best = p;
      }
    });
    return best?.id ?? null;
  }, [packages]);

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Choose a plan that fits your business needs. All plans include a free trial.
        </p>
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-pulse text-gray-500">Loading plans...</div>
        </div>
      ) : packages && packages.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:px-8 py-8 max-w-[1800px] mx-auto">
          {packages.map((pkg: SubscriptionPackage) => (
            <PricingCard
              key={pkg.id}
              pkg={pkg}
              isBest={pkg.id === bestPackageId}
              onSelect={onSelectPlan}
              showSelectButton={!isAuthenticated}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No plans available at the moment. Please check back later.
        </div>
      )}
    </section>
  );
};

export default PricingSection;

